import { Fragment, FormEvent } from "react";
import clsx from "clsx";

import { AssetsPrivateUseModel, PrivateUseModel } from "@interfaces/index";
import {
  formatAmount,
  formatMileagePercentOnEdit,
  getPrivateUseValue,
} from "@utils/index";

export type PrivateUseInputData = {
  index?: string;
  assetId?: string | null;
  categoryId?: string;
  categoryTitle?: string;
  privateUseId?: number | null;
};

export const TabPrivateUse = ({
  privateUses,
  privateUseTotal,
  privateUseTotalPrivateExpenses,
  privateUseTotalBusinessExpenses,
  disabledFeatures,
  isMobile,
  submitIsDisabled,
  inputData,
  inputValue,
  setInputData,
  setInputValue,
  onFormSubmit,
}: {
  privateUses?: PrivateUseModel[];
  privateUseTotal?: string;
  privateUseTotalPrivateExpenses?: string;
  privateUseTotalBusinessExpenses?: string;
  disabledFeatures: string[];
  isMobile: boolean;
  submitIsDisabled: boolean;
  inputData: PrivateUseInputData;
  inputValue: string;
  setInputData: (data: PrivateUseInputData) => void;
  setInputValue: (value: string) => void;
  onFormSubmit: (
    e: FormEvent<HTMLFormElement>,
    asset: AssetsPrivateUseModel,
  ) => void;
}) => (
  <div className="-mx-4 pt-4 md:-mx-10 md:pt-2">
    {!isMobile && (
      <div className="flex h-12 px-10 text-100 text-black-54 *:flex *:w-full *:items-center *:justify-center">
        <div className="min-w-72 !justify-start pl-4">
          {!disabledFeatures.includes("Assets") ? "Category/Asset" : "Category"}
        </div>
        <div className="min-w-40">Total Amount</div>
        <div className="min-w-40">Private Use %</div>
        <div className="min-w-40">Private Use</div>
        <div className="min-w-40 !justify-end pr-4">Business Expenses</div>
      </div>
    )}
    {privateUses?.map(
      ({ categoryId, categoryTitle, assets }, categoryIndex) => (
        <Fragment key={categoryId}>
          <div
            className={clsx(
              "border-t-black-4 bg-black-4 px-6 py-4 text-black-54 md:mx-10 md:border-t md:bg-lightGray md:px-4 md:text-black-100",
              (!assets?.[0]?.assetId || disabledFeatures.includes("Assets")) &&
                "md:hidden",
            )}
          >
            {categoryTitle}
          </div>
          {assets.map((asset, index) => (
            <div
              key={`${categoryId}---${asset.assetId}`}
              className="m-4 flex flex-col justify-between rounded-lg border border-black-8 p-4 font-bold text-black-100 md:m-0 md:mx-10 md:h-[68px] md:flex-row md:justify-start md:rounded-none md:border-0 md:p-0 md:font-normal"
              data-testid={
                categoryIndex <= 0 && index <= 0
                  ? "table_privateUseTable"
                  : undefined
              }
            >
              <div
                className={clsx(
                  "flex flex-col gap-y-1 md:w-full md:min-w-72 md:justify-center md:border-r md:border-t md:border-r-black-8 md:border-t-black-8 md:pl-4",
                  (!asset.assetId || disabledFeatures.includes("Assets")) &&
                    "md:bg-lightGray",
                )}
              >
                {!disabledFeatures.includes("Assets") && (
                  <div className="text-100 font-normal text-black-54 md:hidden">
                    Asset
                  </div>
                )}
                {!disabledFeatures.includes("Assets") && (
                  <div className={clsx(!asset.assetId && "md:hidden")}>
                    {asset.assetName}
                  </div>
                )}
                <div
                  className={clsx(
                    "hidden",
                    (!asset.assetId || disabledFeatures.includes("Assets")) &&
                      "md:block",
                  )}
                >
                  {categoryTitle}
                </div>
              </div>
              <div
                className={clsx(
                  "flex items-center justify-between md:mt-0 md:w-full md:min-w-40 md:justify-center md:border-r md:border-t md:border-r-black-8 md:border-t-black-8",
                  !asset.assetId || disabledFeatures.includes("Assets")
                    ? "mt-0"
                    : "mt-6",
                )}
              >
                <div className="flex flex-col gap-y-1">
                  <div className="text-100 font-normal text-black-54 md:hidden">
                    Total Amount
                  </div>
                  <div className="text-300 md:text-200">
                    {formatAmount(asset.totalAmount, "£")}
                  </div>
                </div>
                <div className="flex flex-col gap-y-1 md:hidden">
                  <div className="text-100 font-normal text-black-54">
                    Private Use Total
                  </div>
                  <div className="text-right text-black-70">
                    {formatAmount(asset.privateExpenses, "£")}
                  </div>
                </div>
                <div className="flex flex-col gap-y-1 md:hidden">
                  <div className="text-100 font-normal text-black-54">
                    Business Expenses
                  </div>
                  <div className="text-right text-black-70">
                    {formatAmount(asset.businessExpenses, "£")}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-col items-start gap-y-0.5 md:mt-0 md:w-full md:min-w-40 md:flex-row md:items-center md:justify-center md:border-r md:border-t md:border-r-black-8 md:border-t-black-8">
                <div className="text-100 font-normal text-black-54 md:hidden">
                  Private Use %
                </div>
                {inputData.index === `${categoryId}___${index}` ? (
                  <form onSubmit={(e) => onFormSubmit(e, asset)}>
                    <input
                      name="privateUse"
                      className="flex h-12 w-full items-center rounded-lg border border-black-32 px-4 text-right outline-none md:w-20"
                      autoFocus
                      value={inputValue}
                      onChange={({ currentTarget }) =>
                        setInputValue(
                          formatMileagePercentOnEdit(currentTarget.value),
                        )
                      }
                      onBlur={(e) => {
                        if (
                          e.currentTarget.value !== getPrivateUseValue(asset)
                        ) {
                          e.currentTarget.form?.requestSubmit();
                        } else {
                          setInputData({});
                        }
                      }}
                    />
                  </form>
                ) : (
                  <div
                    className={clsx(
                      "flex h-12 w-full items-center justify-end rounded-lg border border-black-16 px-4 hover:cursor-pointer md:w-20",
                      submitIsDisabled ||
                        (!(asset.policy.canCreate && !asset.privateUseId) &&
                          !(asset.policy.canUpdate && asset.privateUseId))
                        ? "pointer-events-none bg-black-4 text-black-54"
                        : "",
                    )}
                    onClick={() => {
                      setInputData({
                        index: `${categoryId}___${index}`,
                        assetId: asset.assetId,
                        categoryId,
                        categoryTitle,
                        privateUseId: asset.privateUseId,
                      });
                      setInputValue(getPrivateUseValue(asset));
                    }}
                  >
                    {getPrivateUseValue(asset, "0")}%
                  </div>
                )}
              </div>
              <div className="hidden w-full min-w-40 items-center justify-center border-r border-t border-r-black-8 border-t-black-8 md:flex">
                {formatAmount(asset.privateExpenses, "£")}
              </div>
              <div className="hidden w-full min-w-40 items-center justify-end border-t border-t-black-8 pr-4 md:flex">
                {formatAmount(asset.businessExpenses, "£")}
              </div>
            </div>
          ))}
          {!disabledFeatures.includes("Assets") &&
            categoryTitle === "Motor" && (
              <>
                <div className="mx-10 hidden h-px bg-black-8 md:block" />
                <div
                  key="titles"
                  className="mt-8 hidden h-12 px-10 text-100 text-black-54 *:flex *:w-full *:items-center *:justify-center md:flex"
                >
                  <div className="min-w-72 !justify-start pl-4">Category</div>
                  <div className="min-w-40">Total Amount</div>
                  <div className="min-w-40">Private Use %</div>
                  <div className="min-w-40">Private Use</div>
                  <div className="min-w-40 !justify-end pr-4">
                    Business Expenses
                  </div>
                </div>
              </>
            )}
        </Fragment>
      ),
    )}
    <div className="mx-10 hidden h-px bg-black-16 md:block" />
    <div className="flex md:mx-10">
      <div className="hidden min-w-72 md:block">
        <div className="h-[53px] bg-lightGray" />
        <div className="h-[68px] border-b border-l border-b-black-8 border-l-black-8" />
      </div>
      <div className="hidden w-full min-w-40 md:block">
        <div className="flex h-[53px] items-center justify-center bg-lightGray">
          Total
        </div>
        <div className="flex h-[68px] items-center justify-center border-b border-b-black-8">
          {formatAmount(privateUseTotal, "£")}
        </div>
      </div>
      <div className="hidden w-full min-w-40 md:block">
        <div className="h-[53px] bg-lightGray" />
        <div className="h-[68px] border-b border-b-black-8" />
      </div>
      <div className="hidden w-full min-w-40 md:block">
        <div className="flex h-[53px] items-center justify-center bg-lightGray">
          Total
        </div>
        <div className="flex h-[68px] items-center justify-center border-b border-b-black-8">
          {formatAmount(privateUseTotalPrivateExpenses, "£")}
        </div>
      </div>
      <div className="w-full md:min-w-40">
        <div className="bg-black-4 px-6 py-4 text-right text-black-54 md:bg-lightGray md:px-4 md:text-black-100">
          Total
        </div>
        <div className="flex items-center justify-end border border-black-8 p-4 font-bold text-black-100 md:h-[68px] md:border-l-0 md:px-4 md:py-0">
          {formatAmount(privateUseTotalBusinessExpenses, "£")}
        </div>
      </div>
    </div>
  </div>
);
